import { ChangeEvent, useMemo } from "react";
import { SelectOption } from "@idw-types/common";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import {
    FormattedIdentityProviderDetails,
    IdentityProviderScheme
} from "@idw-types/data/identity-providers";
import { IdentityProviderCompanyIDPValidationErrors } from "@idw-types/common/validation/identity-provider/identity-provider-validation-errors";
import { InputFormGroup } from "../../common/input-form-group/input-form-group";
import { SelectFormGroup } from "../../common/select-form-group/select-form-group";
import { ContentPageHeader } from "../../common/content-page-header/content-page-header";
import { ContentPageBody } from "../../common/content-page-body/content-page-body";
import styles from "../identity-provider-shared-style.less";

type IdentityProviderInfoContainerProps = {
    isDisabled: boolean;
    labelWidth: LabelWidthTypes;
    schemes: Array<IdentityProviderScheme>;
    validationErrors: IdentityProviderCompanyIDPValidationErrors;
    state: FormattedIdentityProviderDetails;
    setState: (state: FormattedIdentityProviderDetails) => void;
};

export const IdentityProviderCompanyContainer = (props: IdentityProviderInfoContainerProps) => {
    const { isDisabled, labelWidth, schemes, validationErrors, state, setState } = props;

    const handleScopeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            companyIDPInfo: {
                ...state.companyIDPInfo,
                scope: event.target.value
            }
        });
    };

    const handleClientIdChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            companyIDPInfo: {
                ...state.companyIDPInfo,
                clientID: event.target.value
            }
        });
    };

    const handleClientSecretChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            companyIDPInfo: {
                ...state.companyIDPInfo,
                clientSecret: event.target.value
            }
        });
    };

    const handleAuthorizationUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            companyIDPInfo: {
                ...state.companyIDPInfo,
                authURL: event.target.value
            }
        });
    };

    const handleTokenUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            companyIDPInfo: {
                ...state.companyIDPInfo,
                tokenURL: event.target.value
            }
        });
    };

    const handleUserInfoUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            companyIDPInfo: {
                ...state.companyIDPInfo,
                userInfoURL: event.target.value
            }
        });
    };

    const handleUserCredentialChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            companyIDPInfo: {
                ...state.companyIDPInfo,
                userCredentialField: event.target.value
            }
        });
    };

    const handleJwksUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            companyIDPInfo: {
                ...state.companyIDPInfo,
                jwksURL: event.target.value
            }
        });
    };

    const handleWellknownUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            companyIDPInfo: {
                ...state.companyIDPInfo,
                wellKnownURL: event.target.value
            }
        });
    };

    const handleTokenAuthenticationSchemeChange = (newValue: SelectOption) => {
        setState({
            ...state,
            companyIDPInfo: {
                ...state.companyIDPInfo,
                tokenAuthScheme: schemes.find(scheme => scheme.id === newValue.value)
            }
        });
    };

    const schemeOptions = useMemo(() => schemes.map(scheme => ({
        label: scheme.scheme,
        value: scheme.id
    })), [schemes]);

    const selectedSchemeOption = useMemo(() => {
        if (state.companyIDPInfo.tokenAuthScheme) {
            return {
                label: state.companyIDPInfo.tokenAuthScheme.scheme,
                value: state.companyIDPInfo.tokenAuthScheme.id
            };
        }
        return null;
    }, [state.companyIDPInfo.tokenAuthScheme]);

    const resource = resources.pages.identityProviderDetails.tabs.organization;

    return (
        <div className={styles.identityProviderDetailsSection}>
            <ContentPageHeader title={resource.header} />
            <ContentPageBody isMaxWidth={true}>
                <InputFormGroup
                    controlId="identity-provider-scope-input"
                    automationId="identity-provider-scope-input"
                    label={resource.inputs.scope.label}
                    ariaLabel={resource.inputs.scope.label}
                    value={state.companyIDPInfo.scope || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    onChange={handleScopeChange}
                    labelWidth={labelWidth}
                />
                {validationErrors.scopeError}
                <InputFormGroup
                    controlId="identity-provider-client-id-input"
                    automationId="identity-provider-client-id-input"
                    label={resource.inputs.clientId.label}
                    ariaLabel={resource.inputs.clientId.label}
                    value={state.companyIDPInfo.clientID || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    onChange={handleClientIdChange}
                    labelWidth={labelWidth}
                />
                {validationErrors.clientIdError}
                <InputFormGroup
                    controlId="identity-provider-client-secret-input"
                    automationId="identity-provider-client-secret-input"
                    label={resource.inputs.clientSecret.label}
                    ariaLabel={resource.inputs.clientSecret.label}
                    value={state.companyIDPInfo.clientSecret || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    onChange={handleClientSecretChange}
                    labelWidth={labelWidth}
                />
                {validationErrors.clientSecretError}
                <InputFormGroup
                    controlId="identity-provider-authorization-url-input"
                    automationId="identity-provider-authorization-url-input"
                    label={resource.inputs.authorizationUrl.label}
                    ariaLabel={resource.inputs.authorizationUrl.label}
                    value={state.companyIDPInfo.authURL || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    onChange={handleAuthorizationUrlChange}
                    labelWidth={labelWidth}
                />
                {validationErrors.authorizationURLError}
                <InputFormGroup
                    controlId="identity-provider-token-url-input"
                    automationId="identity-provider-token-url-input"
                    label={resource.inputs.tokenUrl.label}
                    ariaLabel={resource.inputs.tokenUrl.label}
                    value={state.companyIDPInfo.tokenURL || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    onChange={handleTokenUrlChange}
                    labelWidth={labelWidth}
                />
                {validationErrors.tokenURLError}
                <InputFormGroup
                    controlId="identity-provider-user-info-url-input"
                    automationId="identity-provider-user-info-url-input"
                    label={resource.inputs.userInfoUrl.label}
                    ariaLabel={resource.inputs.userInfoUrl.label}
                    value={state.companyIDPInfo.userInfoURL || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    onChange={handleUserInfoUrlChange}
                    labelWidth={labelWidth}
                />
                {validationErrors.userInfoURLError}
                <InputFormGroup
                    controlId="identity-provider-user-credential-input"
                    automationId="identity-provider-user-credential-input"
                    label={resource.inputs.userCredential.label}
                    ariaLabel={resource.inputs.userCredential.label}
                    value={state.companyIDPInfo.userCredentialField || ""}
                    disabled={isDisabled}
                    isRequired={false}
                    onChange={handleUserCredentialChange}
                    labelWidth={labelWidth}
                />
                <InputFormGroup
                    controlId="identity-provider-jwks-url-input"
                    automationId="identity-provider-jwks-url-input"
                    label={resource.inputs.jwksUrl.label}
                    ariaLabel={resource.inputs.jwksUrl.label}
                    value={state.companyIDPInfo.jwksURL || ""}
                    disabled={isDisabled}
                    isRequired={false}
                    onChange={handleJwksUrlChange}
                    labelWidth={labelWidth}
                />
                <InputFormGroup
                    controlId="identity-provider-wellknown-url-input"
                    automationId="identity-provider-wellknown-url-input"
                    label={resource.inputs.wellknownUrl.label}
                    ariaLabel={resource.inputs.wellknownUrl.label}
                    value={state.companyIDPInfo.wellKnownURL || ""}
                    disabled={isDisabled}
                    isRequired={false}
                    onChange={handleWellknownUrlChange}
                    labelWidth={labelWidth}
                />
                <SelectFormGroup
                    controlId="identity-provider-scheme-select"
                    automationId="identity-provider-scheme-select"
                    ariaLabel={resource.inputs.tokenAuthenticationScheme.label}
                    label={resource.inputs.tokenAuthenticationScheme.label}
                    labelWidth={labelWidth}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={false}
                    isSearchable={false}
                    onChange={handleTokenAuthenticationSchemeChange}
                    options={schemeOptions}
                    selectedOption={selectedSchemeOption}
                />
            </ContentPageBody>
        </div>
    );
};